/**
 * AlertList — elenca gli alert del contratto attivo.
 *
 * Replica del flow mobile Home.js > modal alerts, con differenziazione per
 * `alert.tipo` + `alert.stato`:
 *  - autolettura          → CTA "Invia autolettura" (→ /autolettura)
 *  - bolletta + 'emessa'  → "Nuova bolletta disponibile" → CTA "Vedi bolletta"
 *                            (linka alla bolletta più recente, se nota)
 *  - bolletta + altri     → "Hai bollette da pagare" → CTA "Paga ora"
 *                            (linka alla bolletta più vecchia non pagata)
 *  - altri tipi           → card neutra senza CTA dedicata
 *
 * Niente "marca come letto": gli alert sono attivi finché esistono lato
 * server (es. l'autolettura sparisce quando l'utente la invia).
 */

import {
  AlertCircle,
  ArrowRight,
  BellOff,
  CreditCard,
  Gauge,
  Sparkles,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/card'
import { EmptyState } from '@/components/ui/empty-state'
import type { Bolletta, ContractAlert } from '@/lib/graphql/types'

type Props = {
  alerts: ContractAlert[] | null | undefined
  /**
   * Bollette del contratto attivo. Usate per puntare la CTA dell'alert
   * `bolletta` alla bolletta giusta (la più recente per stato='emessa',
   * la più vecchia non-pagata altrimenti). Se assenti, fallback a /bollette.
   */
  bollette?: Bolletta[]
}

type Tone = 'autolettura' | 'bolletta-new' | 'bolletta-due' | 'generic'

type AlertMeta = {
  icon: typeof Gauge
  title: string
  description: string
  to?: string
  ctaLabel?: string
  tone: Tone
}

const TONE_COLORS: Record<Tone, string> = {
  autolettura: 'bg-secondary/15 text-secondary',
  'bolletta-new': 'bg-success/15 text-success',
  'bolletta-due': 'bg-warning/20 text-warning-foreground',
  generic: 'bg-muted text-muted-foreground',
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}

/** Bolletta target per la CTA dell'alert bolletta. */
function pickInvoiceFor(
  alert: ContractAlert,
  bollette: Bolletta[] | undefined,
): Bolletta | undefined {
  if (!bollette || bollette.length === 0) return undefined
  const stato = (alert.stato ?? '').toLowerCase()
  if (stato === 'emessa') {
    // La più recente (assumiamo bollette ordinate dal backend dalla più
    // recente alla più vecchia, come fa la lista InvoicesPage).
    return bollette[0]
  }
  // Da pagare: scegli la più vecchia non pagata (più urgente).
  const unpaid = bollette.filter(
    (b) => !(b.stato ?? '').toLowerCase().includes('pagat'),
  )
  if (unpaid.length === 0) return undefined
  return unpaid[unpaid.length - 1]
}

function metaFor(alert: ContractAlert, bollette: Bolletta[] | undefined): AlertMeta {
  const t = (alert.tipo || '').toLowerCase()

  if (t.includes('autolettur')) {
    return {
      icon: Gauge,
      title: 'Promemoria autolettura',
      description: 'È aperto il periodo per comunicare la tua autolettura.',
      to: '/autolettura',
      ctaLabel: 'Invia autolettura',
      tone: 'autolettura',
    }
  }

  if (t.includes('bollett')) {
    const stato = (alert.stato ?? '').toLowerCase()
    const target = pickInvoiceFor(alert, bollette)

    if (stato === 'emessa') {
      return {
        icon: Sparkles,
        title: 'Nuova bolletta disponibile',
        description: target
          ? `È stata emessa la bolletta ${target.codice}. Scarica il PDF o consulta il dettaglio.`
          : 'È stata emessa una nuova bolletta sul tuo contratto.',
        to: target
          ? `/bollette/${encodeURIComponent(target.codice)}`
          : '/bollette',
        ctaLabel: target ? 'Vedi bolletta' : 'Vai alle bollette',
        tone: 'bolletta-new',
      }
    }

    return {
      icon: CreditCard,
      title: 'Hai bollette da pagare',
      description: target
        ? `La bolletta ${target.codice} è in attesa di pagamento. Saldala in pochi click.`
        : 'Hai una o più bollette da saldare.',
      to: target
        ? `/pagamenti/${encodeURIComponent(target.codice)}`
        : '/bollette',
      ctaLabel: target ? `Paga ora` : 'Vai alle bollette',
      tone: 'bolletta-due',
    }
  }

  return {
    icon: AlertCircle,
    title: alert.tipo ? capitalize(alert.tipo) : 'Notifica',
    description:
      alert.stato ?? 'Hai una comunicazione importante dal sistema.',
    tone: 'generic',
  }
}

export function AlertList({ alerts, bollette }: Props) {
  if (!alerts || alerts.length === 0) {
    return (
      <EmptyState
        icon={<BellOff className="h-5 w-5" />}
        title="Nessun alert attivo"
        description="Quando ci sarà qualcosa che richiede la tua attenzione comparirà qui."
      />
    )
  }

  return (
    <ul className="flex flex-col gap-3">
      {alerts.map((alert, idx) => {
        const meta = metaFor(alert, bollette)
        const Icon = meta.icon
        const isBollettaDue = meta.tone === 'bolletta-due'
        return (
          <li key={`${alert.tipo}-${alert.stato ?? ''}-${idx}`}>
            <Card className="flex items-start gap-4 p-5">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${TONE_COLORS[meta.tone]}`}
              >
                <Icon className="h-5 w-5" />
              </div>
              <div className="flex flex-1 flex-col gap-1 min-w-0">
                <h3 className="font-semibold text-foreground">{meta.title}</h3>
                <p className="text-sm text-muted-foreground">{meta.description}</p>
                {meta.to && meta.ctaLabel && (
                  <Link
                    to={meta.to}
                    className={
                      isBollettaDue
                        ? 'mt-2 inline-flex w-fit items-center gap-2 rounded-lg bg-warning px-3 py-1.5 text-sm font-semibold text-warning-foreground hover:bg-warning/90'
                        : 'mt-2 inline-flex w-fit items-center gap-1 text-sm font-semibold text-secondary hover:underline'
                    }
                  >
                    {meta.ctaLabel}
                    <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                )}
              </div>
            </Card>
          </li>
        )
      })}
    </ul>
  )
}
